/**
 * OfflineQueueIndicator - Shows offline queue status in UI
 * 
 * Features:
 * - Badge showing pending count
 * - Dropdown with queue details
 * - Manual sync/retry buttons
 * - Online/offline status
 */

import React, { useState } from 'react';
import { 
  CloudOff, 
  Cloud, 
  RefreshCw, 
  Check, 
  AlertTriangle, 
  Image, 
  FileText,
  Trash2,
  RotateCcw,
  Loader2,
  WifiOff,
  Wifi
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { useOfflineQueue, QUEUE_STATUS } from '../lib/useOfflineQueue';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

const OfflineQueueIndicator = () => {
  const { api } = useAuth();
  const {
    isOnline,
    isSyncing,
    stats,
    pendingItems,
    triggerSync,
    retry,
    remove,
    hasPending,
    hasFailed
  } = useOfflineQueue(api);
  
  const [open, setOpen] = useState(false);
  
  const handleSync = async () => {
    toast.info('Syncing offline items...');
    const result = await triggerSync();
    if (result?.totalSynced > 0) {
      toast.success(`Synced ${result.totalSynced} item(s)`);
    } else if (result?.totalFailed > 0) {
      toast.error(`Failed to sync ${result.totalFailed} item(s)`);
    } else {
      toast.info('Nothing to sync');
    }
  };
  
  const handleRetry = async (type, id) => {
    await retry(type, id);
    toast.info('Retrying...');
  };
  
  const handleRemove = async (type, id) => {
    await remove(type, id);
    toast.success('Item removed from queue');
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case QUEUE_STATUS.PENDING:
        return <CloudOff className="w-3 h-3 text-amber-400" />;
      case QUEUE_STATUS.UPLOADING:
        return <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />;
      case QUEUE_STATUS.UPLOADED:
        return <Check className="w-3 h-3 text-emerald-400" />;
      case QUEUE_STATUS.FAILED:
        return <AlertTriangle className="w-3 h-3 text-red-400" />;
      default:
        return null;
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case QUEUE_STATUS.PENDING:
        return 'Pending upload';
      case QUEUE_STATUS.UPLOADING:
        return 'Uploading...';
      case QUEUE_STATUS.UPLOADED:
        return 'Uploaded';
      case QUEUE_STATUS.FAILED:
        return 'Failed - tap to retry';
      default:
        return status;
    }
  };
  
  // Don't show if nothing in queue and online
  if (!hasPending && !hasFailed && isOnline) {
    return null;
  }
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`relative h-9 px-2 ${!isOnline ? 'text-amber-400' : hasFailed ? 'text-red-400' : 'text-zinc-400'}`}
          data-testid="offline-queue-indicator"
        >
          {isOnline ? (
            <Cloud className="w-5 h-5" />
          ) : (
            <CloudOff className="w-5 h-5" />
          )}
          
          {/* Badge for pending/failed count */}
          {(hasPending || hasFailed) && (
            <Badge 
              className={`absolute -top-1 -right-1 h-5 min-w-5 px-1 text-xs ${
                hasFailed ? 'bg-red-500' : 'bg-amber-500'
              }`}
            >
              {stats?.totalPending + stats?.totalFailed || 0}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-[#18181b] border-[#3f3f46] text-white"
      >
        {/* Header */}
        <div className="p-3 border-b border-[#3f3f46]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <Wifi className="w-4 h-4 text-emerald-400" />
              ) : (
                <WifiOff className="w-4 h-4 text-amber-400" />
              )}
              <span className="font-medium">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-zinc-600"
              onClick={handleSync}
              disabled={!isOnline || isSyncing}
            >
              {isSyncing ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              Sync Now
            </Button>
          </div>
          
          {!isOnline && (
            <p className="text-xs text-amber-400 mt-2">
              Your changes are saved locally and will sync when you're back online.
            </p>
          )}
        </div>
        
        {/* Stats */}
        <div className="p-3 grid grid-cols-2 gap-2 text-xs border-b border-[#3f3f46]">
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-400">Photos:</span>
            <span className="text-white">{stats?.photos?.pending || 0} pending</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-400">Incidents:</span>
            <span className="text-white">{stats?.incidents?.pending || 0} pending</span>
          </div>
        </div>
        
        {/* Queue Items */}
        {(pendingItems?.photos?.length > 0 || pendingItems?.incidents?.length > 0) ? (
          <div className="max-h-64 overflow-y-auto">
            {/* Photos */}
            {pendingItems?.photos?.map((photo) => (
              <div 
                key={`photo-${photo.id}`}
                className="p-2 flex items-center gap-2 hover:bg-[#09090b] border-b border-[#3f3f46]/50"
              >
                <Image className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{photo.file_name}</p>
                  <div className="flex items-center gap-1 text-xs">
                    {getStatusIcon(photo.status)}
                    <span className="text-zinc-500">{getStatusText(photo.status)}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {photo.status === QUEUE_STATUS.FAILED && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => handleRetry('photo', photo.id)}
                    >
                      <RotateCcw className="w-3 h-3 text-amber-400" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => handleRemove('photo', photo.id)}
                  >
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
            
            {/* Incidents */}
            {pendingItems?.incidents?.map((incident) => (
              <div 
                key={`incident-${incident.id}`}
                className="p-2 flex items-center gap-2 hover:bg-[#09090b] border-b border-[#3f3f46]/50"
              >
                <FileText className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{incident.title}</p>
                  <div className="flex items-center gap-1 text-xs">
                    {getStatusIcon(incident.status)}
                    <span className="text-zinc-500">{getStatusText(incident.status)}</span>
                    {incident.photos?.length > 0 && (
                      <span className="text-zinc-600">• {incident.photos.length} photo(s)</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {incident.status === QUEUE_STATUS.FAILED && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => handleRetry('incident', incident.id)}
                    >
                      <RotateCcw className="w-3 h-3 text-amber-400" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => handleRemove('incident', incident.id)}
                  >
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-zinc-500 text-sm">
            {isOnline ? 'All items synced' : 'No pending items'}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OfflineQueueIndicator;
